import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Alert } from '@patternfly/react-core'
import {
  Button,
  Popover 
} from '@patternfly/react-core';

import Timestamp from "react-timestamp";
import { apiCheckAuthorization, apiFetchBusinessData, BulkCheckItem, getNamespaceCost } from '../GranularDeploy/helpers/apiHelpers'
import { useAccessToken } from '@app/auth';
import { ExternalLinkSquareAltIcon } from '@patternfly/react-icons'
import { NamespaceResourcesTable } from './NamespaceResourcesTable';
import { PreviewSessionsCard } from './PreviewSessionsCard';
import { Namespace } from '@app/Dashboard/models';
import { computeRequiredMap } from '../GranularDeploy/helpers/labels';
import { formatTeamNameForDeploy } from '../GranularDeploy/helpers/teamFormatter';


interface NamespaceDetailsProps {
  namespace: Namespace,
}

export const accessDeniedMessage =
  'Not authorized to deploy. Please request access via the link below.';

export const NamespaceDetails: React.FC<NamespaceDetailsProps> = ({ namespace }) => {
  const history = useHistory()
  const tokenGetter = useAccessToken();
  const [authError, setAuthError] = useState<string|null>(null)
  const [namespaceCostDaily, setNamespaceCostDaily] = useState<number | null>(null);
  const [namespaceCostMonthly, setNamespaceCostMonthly] = useState<number | null>(null);
  const [isNamespaceCostLoading, setIsNamespaceCostLoading] = useState(false);

  // 2) collect *all* outdated resources
    const outdated = namespace.resources.filter(r => r.status === 'outdated')

  const handleRefreshEnv = async () => {
    setAuthError(null)

    if (!namespace.resources.some(r => r.status === 'outdated')) return;

    // 1) guard on auth as before
    const allowed = await apiCheckAuthorization(tokenGetter)
    if (!allowed) {
      setAuthError(accessDeniedMessage)
      return
    }

    // 3) pick a branch to fetch metadata on (all outdated share the same deploy.branch)
    const branch = outdated[0].deploy.branch || 'master'
    let meta
    try {
      meta = await apiFetchBusinessData(branch, tokenGetter)
    } catch (err) {
      setAuthError(`Could not fetch metadata for branch "${branch}"`)
      return
    }

    // 4) build a Set of all metadata-known app names
    const known = new Set(meta.releases.map((r: any) => r.name))

    // 5) filter your outdated to only those in the metadata
    const validOutdated = outdated.filter(r => known.has(r.name))
    if (validOutdated.length === 0) {
      setAuthError('No valid apps found in metadata to refresh')
      return
    }

    const folderMap: Record<string,string> = {}
      meta.releases.forEach((rel: any) => {
        const folder = rel.metadata?.centralized_repo_folder_name
        if (folder) folderMap[rel.name] = folder
      })

      // 6) map *only* those valid apps to BulkCheckItem
      const items: BulkCheckItem[] = validOutdated.map(r => {
    // if you have git metadata baked in, use it first…
        const raw: any = (r as any).raw?.() || {};
        const gitBranch = raw.git?.branch;
        // otherwise fall back to the deploy.branch
        const branch = gitBranch ?? r.deploy.branch ?? 'master';

        return {
          appName:  r.name,
          repoName: folderMap[r.name] ?? r.name,
          branch
        };
      });

  const labelsSnapshot: Record<string, { requiredLabels: Record<string, boolean>, found: {name:string; value:string}[] }> = {};
    validOutdated.forEach(r => {
      const rel = meta.releases.find((x: any) => x.name === r.name) || {};
      const found = Array.isArray(rel.labels) ? rel.labels : [];
      labelsSnapshot[r.name] = { requiredLabels: computeRequiredMap(found), found };
    });

    const refreshPayload = {
      items,                         // BulkCheckItem[]
      namespace: namespace.name,
      team: namespace.team,
      branch,                        // feature/metadata branch
      labelsSnapshot                 // persisted required-labels booleans
    };

    localStorage.setItem('refreshPayload', JSON.stringify(refreshPayload));
    history.push('/granular-deploy?refresh=true');
}

  const QuickAccessLinks = [
    {title: 'Refresh environment with latest updates', onClick: handleRefreshEnv, condition: ns => outdated.length > 0, color: 'warning' as const},
    { 'title': 'Troubleshoot', 'link': 'https://teladochealth.atlassian.net/wiki/x/BN-qCw', 'condition': (namespace) => ['failing', 'unknown', 'warning'].includes(namespace.status), 'color': 'warning' as const },
    { 'title': 'Reset database', 'link': globalThis.cluster.JENKINS_DEPLOY_URL + '/job/db_reset/build', 'condition': (namespace) => namespace.status != 'stopped', 'color': 'primary' as const },
    { 'title': 'Wake up', 'link': globalThis.cluster.JENKINS_DEPLOY_URL + '/job/env.wakeup/build', 'condition': (namespace) => namespace.status == 'stopped', 'color': 'primary' as const },
    { 'title': 'Delete environment', 'link': globalThis.cluster.JENKINS_DEPLOY_URL + '/job/env.down/build', 'condition': (namespace) => namespace.class == "ephemeral", 'color': 'primary' as const }
  ]

  useEffect(() => {
    setIsNamespaceCostLoading(true);

    getNamespaceCost(namespace.name, tokenGetter).then(cost => {
      setNamespaceCostDaily(cost?.daily ?? null)
      setNamespaceCostMonthly(cost?.accumulated_month ?? null)
    }).catch(err => {
      setNamespaceCostDaily(null)
      setNamespaceCostMonthly(null)
    }).finally(() => {
      setIsNamespaceCostLoading(false)
    })
    
  }, [namespace.name, namespace.team, tokenGetter]);

  return (
    <>
     {authError && (
        <Alert
          variant="danger"
          isInline
          title={authError}
          actionLinks={
            <a href="https://euewizard.docs.teladoc.dev/#/pages/dev?id=access-to-controller" target="_blank" rel="noopener noreferrer">
              Read Documentation
            </a>
          }
          style={{ marginBottom: '1rem' }}
        />
      )}

    <div className="dx-grid dx-grid--gutter">
      <div className="dx-grid-span-4">
        <div className="dx-grid dx-grid--gutter">
          <div className="dx-grid-span-12">
            <div className="dx-card">
              <div className="dx-card__body">
                <dl className="dx-description-list">
                  <div className="dx-dl-group">
                    <dt className="dx-dl-term">
                      Environment class
                    </dt>
                    <dd className="dx-dl-desc">
                      {namespace.class}
                    </dd>
                  </div>
                  <div className="dx-dl-group">
                    <dt className="dx-dl-term">
                      Last request
                    </dt>
                    <dd className="dx-dl-desc">
                      {namespace.requests.last_request ? 
                    <Timestamp relative date={namespace.requests.last_request} options={{ includeDay: false }} />
                        :
                        "No data"
                  }
                    </dd>
                  </div>
                  <div className="dx-dl-group">
                    <dt className="dx-dl-term">
                      Requests in the lasts {namespace.requests.period}
                    </dt>
                    <dd className="dx-dl-desc">
                      {namespace.requests.count}
                    </dd>
                  </div>
                  <div className="dx-dl-group">
                    <dt className="dx-dl-term">
                      Current cost
                    </dt>
                    <dd className="dx-dl-desc">
                      {isNamespaceCostLoading
                        ? 'Loading...'
                        : namespaceCostDaily !== null
                          ? (namespaceCostDaily === 0 && (namespaceCostMonthly ?? 0) === 0
                              ? 'Not enough data'
                              : `$${namespaceCostDaily.toFixed(2)}/day, $${(namespaceCostMonthly ?? 0).toFixed(2)}/month`)
                          : 'No data'}
                    </dd>
                  </div>
                  <div className="dx-dl-group">
                    <dt className="dx-dl-term">
                      Latest env.up job
                    </dt>
                    <dd className="dx-dl-desc">
                    <Button size="sm" isBlock component='a' target="_blank" rel="noopener noreferrer" iconPosition="end" href={namespace.jenkins} icon={<ExternalLinkSquareAltIcon />}>Jenkins</Button>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
          {QuickAccessLinks.some((link) => link.condition(namespace)) ?
              <div className="dx-grid-span-12">
                <div className="dx-card">
                  <div className="dx-card__title">
                    Quick access
                  </div>
                  <div className="dx-card__body">
                    <div className="dx-stack dx-stack--gutter">
                      {QuickAccessLinks
                        .filter(link => link.condition(namespace))
                        .map(link => (
                          <div key={link.title}>
                            {link.onClick ? (
                (
                                <Button
                                  component="button"
                                  onClick={link.onClick}
                                  variant={link.color}
                                  isBlock
                                  size="sm"
                                >
                                  {link.title}
                                </Button>
                              )
                            ) : (
                              <Button
                                component="a"
                                href={link.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                icon={<ExternalLinkSquareAltIcon />}
                                iconPosition="end"
                                variant={link.color}
                                isBlock
                                size="sm"
                              >
                                {link.title}
                              </Button>
                            )}
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </div>
              </div>
              : undefined
          }

          {(namespace.previewSessionOverlays?.length ?? 0) > 0 ?
              <div className="dx-grid-span-12">
                <PreviewSessionsCard namespace={namespace} />
              </div>
              : undefined
          }

        </div>
      </div>



      <div className="dx-grid-span-8 dx-grid-row-span-2">
        <div className="dx-card">
          <div className="dx-card__title">Resources</div>
          <NamespaceResourcesTable namespace={namespace}/>

        </div>
      </div>
    </div>
  </>
  )
}
